import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, injectIntl } from 'react-intl-next';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
} from 'react-apollo';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener';
import {
	WATCH_CONTENT_SHORTCUT,
	UNIVERSAL_WATCH_CONTENT_SHORTCUT,
	UNIVERSAL_WATCH_CONTENT_SHORTCUT_SAFARI,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts';
import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage';
import { isSafari } from '@confluence/browser-helper';

import { WatchDialogUnwatchContentMutation } from './WatchDialogUnwatchContentMutation.graphql';
import { WatchDialogWatchContentMutation } from './WatchDialogWatchContentMutation.graphql';
import { ContentType } from './ContentType.ts';
import { UpdateContentType, UpdateType } from './UpdateType.ts';

const i18n = defineMessages({
	watchPageNotification: {
		id: 'watch-dialog.notification.start.watching.page',
		defaultMessage: 'You started watching this content.',
		description: 'Message shown on flag when using shortcut to start watching page notification',
	},
	unwatchPageNotification: {
		id: 'watch-dialog.notification.stop.watching.page',
		defaultMessage: 'You stopped watching this content.',
		description: 'Message shown on flag when using shortcut to stop watching page notification',
	},
	watchBlogNotification: {
		id: 'watch-dialog.notification.start.watching.blog',
		defaultMessage: 'You started watching this blog.',
		description: 'Message shown on flag when using shortcut to start watching blog notification',
	},
	unwatchBlogNotification: {
		id: 'watch-dialog.notification.stop.watching.blog',
		defaultMessage: 'You stopped watching this blog.',
		description: 'Message shown on flag when using shortcut to stop watching blog notification',
	},
	watchWhiteboardNotification: {
		id: 'watch-dialog.notification.start.watching.whiteboard',
		defaultMessage: 'You started watching this whiteboard.',
		description:
			'Message shown on flag when using shortcut to start watching whiteboard notification',
	},
	unwatchWhiteboardNotification: {
		id: 'watch-dialog.notification.stop.watching.whiteboard',
		defaultMessage: 'You stopped watching this whiteboard.',
		description:
			'Message shown on flag when using shortcut to stop watching whiteboard notification',
	},
	watchDatabaseNotification: {
		id: 'watch-dialog.notification.start.watching.database',
		defaultMessage: 'You started watching this database.',
		description:
			'Message shown on flag when using shortcut to start watching database notification',
	},
	unwatchDatabaseNotification: {
		id: 'watch-dialog.notification.stop.watching.database',
		defaultMessage: 'You stopped watching this database.',
		description: 'Message shown on flag when using shortcut to stop watching database notification',
	},
});

const WatchDialogShortcutHandler = ({
	contentId,
	contentType,
	params,
	flagsContainer,
	openDialog,
	updateQueryHandler,
	intl,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [{ isEditMode: isLiveEditMode }] = useLivePageMode();
	const { isWatchingContent, isWatchingChildren, hasAncestorWatchingChildren, isWatchingSpace } =
		params;
	const isOnEditRoute = useIsEditorPage();

	const fireAnalyticsEvent = (action, actionSubject) => {
		const analyticsData = {
			action,
			actionSubject,
			actionSubjectId: contentId,
			objectId: contentId,
			objectType: contentType,
			source: 'WatchDialogShortcut',
			attributes: {
				componentVersion: 'v2',
			},
		};

		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: analyticsData,
		}).fire();
	};

	const [unwatchContent] = useMutation(WatchDialogUnwatchContentMutation, {
		onCompleted: () => {
			fireAnalyticsEvent(UpdateType.UNWATCH, UpdateContentType.CONTENT);
		},
		update: (cache, { data: { unwatchContent } }) => {
			updateQueryHandler(
				cache,
				UpdateContentType.CONTENT,
				unwatchContent.content.currentUserIsWatching,
			);
		},
	});

	const [watchContent] = useMutation(WatchDialogWatchContentMutation, {
		onCompleted: () => {
			fireAnalyticsEvent(UpdateType.WATCH, UpdateContentType.CONTENT);
		},
		update: (cache, { data: { watchContent } }) => {
			updateQueryHandler(
				cache,
				UpdateContentType.CONTENT,
				watchContent.content.currentUserIsWatching,
			);
		},
	});

	const getWatchNotificationContent = () => {
		switch (contentType) {
			case ContentType.WHITEBOARD:
				return i18n.watchWhiteboardNotification;
			case ContentType.DATABASE:
				return i18n.watchDatabaseNotification;
			case ContentType.BLOG:
				return i18n.watchBlogNotification;
			default:
				return i18n.watchPageNotification;
		}
	};

	const getUnwatchNotificationContent = () => {
		switch (contentType) {
			case ContentType.WHITEBOARD:
				return i18n.unwatchWhiteboardNotification;
			case ContentType.DATABASE:
				return i18n.unwatchDatabaseNotification;
			case ContentType.BLOG:
				return i18n.unwatchBlogNotification;
			default:
				return i18n.unwatchPageNotification;
		}
	};

	const handleShortcutClick = () => {
		const isImplicitlyWatching =
			isWatchingSpace || hasAncestorWatchingChildren || isWatchingChildren;

		if (isImplicitlyWatching) {
			openDialog();
		} else {
			if (isWatchingContent) {
				unwatchContent({ variables: { id: contentId } }).then(() => {
					flagsContainer.showSuccessFlag({
						title: intl.formatMessage(getUnwatchNotificationContent()),
						close: 'auto',
					});
				});
			} else {
				watchContent({ variables: { id: contentId } }).then(() => {
					flagsContainer.showSuccessFlag({
						title: intl.formatMessage(getWatchNotificationContent()),
						close: 'auto',
					});
				});
			}
		}
	};

	return (
		<GeneralShortcutListener
			deprecatedAccelerator={
				!(isLiveEditMode || isOnEditRoute) ? WATCH_CONTENT_SHORTCUT : undefined
			}
			accelerator={
				isSafari() ? UNIVERSAL_WATCH_CONTENT_SHORTCUT_SAFARI : UNIVERSAL_WATCH_CONTENT_SHORTCUT
			}
			listener={handleShortcutClick}
		/>
	);
};

WatchDialogShortcutHandler.propTypes = {
	contentId: PropTypes.string.isRequired,
	contentType: PropTypes.string.isRequired,
	params: PropTypes.shape({
		isWatchingContent: PropTypes.bool.isRequired,
		isWatchingChildren: PropTypes.bool.isRequired,
		hasAncestorWatchingChildren: PropTypes.bool.isRequired,
		isWatchingSpace: PropTypes.bool.isRequired,
	}).isRequired,
	flagsContainer: PropTypes.instanceOf(FlagsStateContainer),
	openDialog: PropTypes.func,
	updateQueryHandler: PropTypes.func,
	intl: PropTypes.object,
};

export const WatchDialogShortcut = injectIntl(WatchDialogShortcutHandler);
